import React from 'react';
import { Typography, Input, Button, Upload, Radio } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useAuthContext } from '../../../context/auth';
import { useUploadMedia } from '../../../hooks/media/use-upload-media';
import { v4 as uuidv4 } from 'uuid';
import { CustomTooltip } from './custom-tooltip';
import { tokens } from '../../../theme/tokens';

const { Text } = Typography;

interface ImageFieldProps {
  field: any;
  value: any;
  onChange: (value: any) => void;
  commonStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  error?: string;
}

export const ImageField: React.FC<ImageFieldProps> = ({ field, value, onChange, commonStyle, labelStyle, error }) => {
  const [imageInputType, setImageInputType] = React.useState<'link' | 'upload'>('link');
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadedFileName, setUploadedFileName] = React.useState<string | null>(null);

  const { userInfo } = useAuthContext();
  const { uploadPosterDirect } = useUploadMedia();

  const clientId = userInfo?.['custom:clientId'];

  const handleUpload = async (file: File) => {
    if (!clientId) {
      console.error('No clientId available');
      return;
    }

    setIsUploading(true);

    try {
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
      const fileName = uuidv4(); // Generar nombre único para la imagen

      const result = await uploadPosterDirect('image', fileExtension, file, fileName, clientId);

      if (result?.publicUrl) {
        onChange(result.publicUrl);
        setUploadedFileName(file.name);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={commonStyle}>
      <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
        {field.label}
      </Text>

      <div style={{ marginBottom: 12 }}>
        <Radio.Group
          value={imageInputType}
          onChange={e => {
            setImageInputType(e.target.value);
            if (e.target.value === 'link') {
              setUploadedFileName(null);
            }
          }}
          style={{ display: 'flex', gap: 8 }}
        >
          <Radio value='link' style={{ fontSize: commonStyle.fontSize }}>
            URL
          </Radio>
          <Radio value='upload' style={{ fontSize: commonStyle.fontSize }}>
            Subir archivo
          </Radio>
        </Radio.Group>
      </div>

      {imageInputType === 'link' ? (
        <CustomTooltip field={field}>
          <Input
            placeholder='https://ejemplo.com/imagen.jpg'
            style={{ fontSize: 12 }}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            status={error ? 'error' : undefined}
          />
        </CustomTooltip>
      ) : (
        <CustomTooltip field={field}>
          <Upload
            accept='image/jpeg,image/jpg,image/png'
            beforeUpload={file => {
              handleUpload(file);
              return false;
            }}
            showUploadList={false}
          >
            <Button
              type='primary'
              icon={<UploadOutlined />}
              loading={isUploading}
              style={{ height: '30px', color: '#000000', backgroundColor: tokens.colors.brands.blue[100] }}
            >
              {isUploading
                ? 'Subiendo...'
                : uploadedFileName
                  ? uploadedFileName.length > 20
                    ? `${uploadedFileName.substring(0, 20)}...`
                    : uploadedFileName
                  : 'Seleccionar imagen JPG/PNG'}
            </Button>
          </Upload>
        </CustomTooltip>
      )}

      {field.helperText && (
        <Text type='secondary' style={{ fontSize: 10, marginTop: 4, display: 'block' }}>
          {Array.isArray(field.helperText) ? field.helperText.join('\u2003•\u2003') : field.helperText}
        </Text>
      )}

      {error && (
        <Text type='danger' style={{ fontSize: 12, marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
};
